import { useDispatch, useSelector } from "react-redux";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import * as React from "react";
import {
  Box,
  ClickAwayListener,
  SxProps,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ListItemIcon from "@mui/material/ListItemIcon";
import { MoreHoriz } from "@mui/icons-material";
import {
  deleteConversation,
  fetchExchanges,
  patchConversation,
  selectConversationAction,
} from "../../store/conversation.tsx";
import { styled } from "@mui/material/styles";
import PinIcon from "../icons/PinIcon.tsx";
import { Conversation } from "../../store/types.tsx";
import DeleteIcon from "../icons/DeleteIcon.tsx";
import RenameIcon from "../icons/RenameIcon.tsx";
import UnpinIcon from "../icons/UnpinIcon.tsx";
import TallyDialog from "../dialog/TallyDialog.tsx";
import { i18n } from "@lingui/core";
import { Trans } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";

interface ConversationDrawerSectionProps {
  conversations: Conversation[];
  title: string;
  toggleConversation: boolean;
}

interface PartialConversation {
  id: string;
  title: string | null;
}

interface StoreState {
  conversationState: { selectedConversation: "" };
}

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#f5f5f9",
    color: "rgba(0, 0, 0, 0.87)",
    maxWidth: 100,
    margin: 0,
    fontSize: theme.typography.pxToRem(13),
    border: "1px solid #dadde9",
    padding: 0,
    borderRadius: "9px",
  },
  [`& .${tooltipClasses.tooltip} > ul > div:first-of-type`]: {
    borderTopLeftRadius: 9,
    borderTopRightRadius: 9,
  },
  [`& .${tooltipClasses.tooltip} > ul > div:last-of-type`]: {
    borderBottomLeftRadius: 9,
    borderBottomRightRadius: 9,
  },
}));

function truncateName(name: string | null): string {
  if (!name) {
    return "";
  }
  if (name.length > 25) {
    return name.slice(0, 23) + "...";
  } else {
    return name;
  }
}

const ConversationDrawerSection: React.FC<ConversationDrawerSectionProps> = (
  props,
) => {
  const dispatch = useDispatch();
  const theme = useTheme();

  const [openTooltip, setOpenTooltip] = React.useState<boolean>(false);
  const [editConversation, setEditConversation] =
    React.useState<PartialConversation | null>(null);
  const [isRenameDialogOpen, setRenameDialogOpen] =
    React.useState<boolean>(false);

  const handleRenameDialogOpen = (
    conversationId: string,
    conversationTitle: string | null,
  ) => {
    setOpenTooltip(false);
    setEditConversation({ id: conversationId, title: conversationTitle });
    setRenameDialogOpen(true);
  };

  // Close the dialog
  const handleRenameDialogClose = () => {
    setRenameDialogOpen(false);
  };

  const handleRenameConversation = async (newConversationTitle: string) => {
    if (
      newConversationTitle !== undefined &&
      editConversation !== null &&
      newConversationTitle.trim() != ""
    ) {
      dispatch(
        patchConversation(
          editConversation.id,
          JSON.stringify({ title: newConversationTitle }),
        ) as any,
      );
      handleRenameDialogClose();
    } else {
    }
  };

  const htmlTooltipListItemProps: SxProps = [
    {
      paddingTop: theme.spacing(0.5),
      paddingBottom: theme.spacing(0.5),
      transition: theme.transitions.create("background-color", {
        duration: theme.transitions.duration.short,
      }),
    },
    {
      "&:hover": {
        backgroundColor: theme.palette.action.focus,
        "& .MuiListItemIcon-root": {
          color: "white",
        },
      },
    },
  ];

  const selectedConversation = useSelector(
    (state: StoreState) => state.conversationState.selectedConversation,
  );

  const handleTooltipClose = () => {
    setOpenTooltip(false);
  };

  const handleTooltipOpen = () => {
    if (openTooltip) {
      setOpenTooltip(false);
    } else {
      setOpenTooltip(true);
    }
  };

  const togglePinConversation = async (
    id: string,
    is_pinned_value: boolean,
  ) => {
    dispatch(
      patchConversation(
        id,
        JSON.stringify({ is_pinned: is_pinned_value }),
      ) as any,
    );
  };

  const backgroundColor: string = useMediaQuery(theme.breakpoints.down("md"))
    ? theme.palette.primary.light
    : theme.palette.primary.dark;
  const color: string = useMediaQuery(theme.breakpoints.down("md"))
    ? theme.palette.common.white
    : theme.palette.primary.main;

  const selectConversation = async (id: string) => {
    dispatch(selectConversationAction(id) as any);
    dispatch(fetchExchanges(id) as any);
  };

  const deleteConversationAction = async (id: string) => {
    dispatch(deleteConversation(id) as any);
  };

  return (
    props.conversations.length > 0 && (
      <div className="conversation-drawer-section">
        <h4 style={{ marginLeft: theme.spacing(1) }}>{props.title}</h4>
        <List>
          {props.conversations.map((conversation) => (
            <ListItem
              key={conversation.id}
              disablePadding
              sx={[
                {
                  alignItems: "flex-end",
                  paddingLeft: theme.spacing(1),
                  paddingRight: theme.spacing(1),
                },
                selectedConversation === conversation.id && {
                  backgroundColor: backgroundColor,
                  color: color,
                  borderRadius: "6px",
                },
              ]}
            >
              <ListItemButton
                sx={{ float: "left", width: "88%" }}
                onClick={() => selectConversation(conversation.id)}
              >
                <ListItemText
                  primary={truncateName(conversation.title)}
                  sx={{}}
                />
              </ListItemButton>
              {selectedConversation === conversation.id ? (
                <ClickAwayListener onClickAway={handleTooltipClose}>
                  {/*
                  DO NOT REMOVE this DIV. It will break the behavior on mobile screens or have unexpected behavior.
                  The reason is that in order for the tooltip to work properly, because the child of a Material-UI Tooltip must be able to hold a ref.
                  https://stackoverflow.com/questions/57527896/material-ui-tooltip-doesnt-display-on-custom-component-despite-spreading-props
                  */}
                  <div className="conversation-drawer-more-button">
                    <HtmlTooltip
                      onClose={handleTooltipClose}
                      title={
                        <React.Fragment>
                          <List
                            sx={{
                              margin: 0,
                              padding: 0,
                            }}
                          >
                            <ListItemButton
                              sx={htmlTooltipListItemProps}
                              onClick={() =>
                                togglePinConversation(
                                  conversation.id,
                                  props.toggleConversation,
                                )
                              }
                            >
                              {props.toggleConversation ? (
                                <PinIcon></PinIcon>
                              ) : (
                                <UnpinIcon></UnpinIcon>
                              )}

                              {props.toggleConversation
                                ? i18n._(
                                    msg({
                                      context: "Button title of the pin button",
                                      message: "Pin",
                                    }),
                                  )
                                : i18n._(
                                    msg({
                                      context:
                                        "Button title of the unpin button",
                                      message: "Unpin",
                                    }),
                                  )}
                            </ListItemButton>
                            <ListItemButton
                              sx={htmlTooltipListItemProps}
                              onClick={() =>
                                handleRenameDialogOpen(
                                  conversation.id,
                                  conversation.title,
                                )
                              }
                            >
                              <RenameIcon />
                              <Trans
                                context={"Button title of the rename button."}
                              >
                                Rename
                              </Trans>
                            </ListItemButton>
                            <ListItemButton
                              sx={htmlTooltipListItemProps}
                              onClick={() =>
                                deleteConversationAction(conversation.id)
                              }
                            >
                              <DeleteIcon />
                              <Trans
                                context={"Button title of the delete button."}
                              >
                                Delete
                              </Trans>
                            </ListItemButton>
                          </List>
                        </React.Fragment>
                      }
                      placement="right-start"
                      disableFocusListener
                      disableHoverListener
                      disableTouchListener
                      open={openTooltip}
                      slotProps={{
                        popper: {
                          sx: {
                            [`&.${tooltipClasses.popper}[data-popper-placement*="right-start"] .${tooltipClasses.tooltip}`]:
                              { marginLeft: "-3px", marginTop: "14px" },
                          },
                        },
                      }}
                    >
                      <ListItemButton
                        onClick={handleTooltipOpen}
                        sx={{
                          float: "right",
                          width: "1.5rem",
                        }}
                      >
                        <ListItemIcon
                          sx={[
                            {
                              color: {
                                xs: theme.palette.common.white,
                                sm: theme.palette.common.white,
                                md: theme.palette.primary.main,
                                lg: theme.palette.primary.main,
                                xl: theme.palette.primary.main,
                              },
                            },
                          ]}
                        >
                          <MoreHoriz />
                        </ListItemIcon>
                      </ListItemButton>
                    </HtmlTooltip>
                  </div>
                </ClickAwayListener>
              ) : (
                <Box />
              )}
            </ListItem>
          ))}
        </List>
        <TallyDialog
          open={isRenameDialogOpen}
          title={i18n._(
            msg({
              context: "Button title of the rename conversation button",
              message: "Rename conversation",
            }),
          )}
          subtitle={i18n._(
            msg({
              context:
                "Span above the title input explaining the limitation of the field bellow.",
              message: "Field cannot be longer than 100 characters",
            }),
          )}
          submitTitle={i18n._(
            msg({
              context: "Button title of the rename conversation button",
              message: "Rename",
            }),
          )}
          onSubmit={handleRenameConversation}
          initialInputValue={editConversation?.title ?? ""}
          handleDialogClose={handleRenameDialogClose}
        />
      </div>
    )
  );
};

export default ConversationDrawerSection;
